import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { trackApiFailed, apiStatus } from "./tracking";
import { recordApiDuration, recordApiError } from "../services/metrics";

export interface QueryState<T> {
  data: T | undefined;
  error: unknown;
  /** True while a request is in flight (including the first load). */
  loading: boolean;
  /** True only before the first result is available — drives skeletons. */
  initialLoading: boolean;
}

/* A process-wide cache keyed by the query key, so navigating between pages
 * doesn't refetch (or flash skeletons for) data already loaded. Stale-while-
 * revalidate: a cached result shows instantly; within DEDUPE_MS it's served as
 * is, after that it's revalidated in the background. Cleared on full reload. */
interface CacheEntry {
  data: unknown;
  ts: number;
}
const cache = new Map<string, CacheEntry>();
const DEDUPE_MS = 60_000;

/* Count of requests in flight, so a refresh affordance can show progress. */
let activeRequests = 0;
const fetchingListeners = new Set<() => void>();

function subscribeFetching(listener: () => void): () => void {
  fetchingListeners.add(listener);
  return () => fetchingListeners.delete(listener);
}

function setActiveRequests(next: number): void {
  activeRequests = next;
  fetchingListeners.forEach((listener) => listener());
}

/** True while any query request is in flight. */
export function useIsFetching(): boolean {
  return useSyncExternalStore(
    subscribeFetching,
    () => activeRequests > 0,
    () => false,
  );
}

/* In-flight requests keyed the same as the cache. When several consumers ask
 * for the same key before the first response lands — a shared metric across
 * panels, or React 18 StrictMode double-invoking the effect in dev — they share
 * one network request instead of each firing their own. Cleared when the
 * request settles. */
const inflight = new Map<string, Promise<unknown>>();

function fetchDeduped<T>(
  key: string,
  fetcher: () => Promise<T>,
  origin?: string,
): Promise<T> {
  const existing = inflight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  setActiveRequests(activeRequests + 1);
  const start = performance.now();
  const p = observe(fetcher(), origin, start).finally(() => {
    setActiveRequests(activeRequests - 1);
    // Only clear if we're still the current in-flight entry (a later refetch
    // for the same key may have replaced us).
    if (inflight.get(key) === p) inflight.delete(key);
  });
  inflight.set(key, p);
  return p;
}

/* Metrics and ApiRequestFailed are recorded on the shared request, not per
 * consumer, so N hooks on one key report one failure. */
function observe<T>(
  request: Promise<T>,
  origin: string | undefined,
  start: number,
): Promise<T> {
  if (!origin) return request;

  return request.then(
    (result) => {
      recordApiDuration(origin, 200, performance.now() - start);
      return result;
    },
    (err: unknown) => {
      const status = apiStatus(err);
      recordApiDuration(origin, status, performance.now() - start);
      recordApiError(origin, status);
      trackApiFailed(origin, err);
      throw err;
    },
  );
}

/* A tiny data-fetching hook (no react-query dependency). Re-runs when `key`
 * changes, dedupes/serves from the cache, ignores out-of-order resolutions, and
 * keeps previous data visible while refetching so changes don't flash empty. */
export function useQuery<T>(
  fetcher: () => Promise<T>,
  key: string,
  /** ApiRequestFailed discriminator (e.g. "loadSummary"). When set, a failed
   *  fetch fires the event once per failure at the data layer. */
  origin?: string,
  /** When false, the hook issues no request and stays idle (data undefined,
   *  loading false). Use it when the fetch is meaningless — e.g. a config read
   *  with no config_id yet — so we don't fire a bogus request (and a spurious
   *  ApiRequestFailed) for input that can't resolve. Defaults to true. */
  enabled = true,
): QueryState<T> {
  // Seed synchronously from cache so a revisit paints data on first render.
  const seeded = enabled
    ? (cache.get(key) as CacheEntry | undefined)
    : undefined;
  const [data, setData] = useState<T | undefined>(
    seeded?.data as T | undefined,
  );
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(enabled && !seeded);
  // Keep the latest fetcher without making it part of the effect's identity.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    // Disabled: no fetch. Clear any in-flight/loading state and stay idle.
    if (!enabled) {
      setLoading(false);
      return;
    }

    let active = true;
    const entry = cache.get(key) as CacheEntry | undefined;

    if (entry) {
      // Serve cache immediately (no skeleton); skip refetch while fresh.
      setData(entry.data as T);
      setError(undefined);
      if (Date.now() - entry.ts < DEDUPE_MS) {
        setLoading(false);
        return;
      }
    } else {
      setData(undefined);
    }

    setLoading(true);
    fetchDeduped(key, fetcherRef.current, origin)
      .then((result) => {
        if (!active) return;

        cache.set(key, { data: result, ts: Date.now() });
        setData(result);
        setError(undefined);
      })
      .catch((err) => {
        // Keep any cached data visible on a background-revalidation failure.
        if (!active) return;

        setError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [key, origin, enabled]);

  return {
    data,
    error,
    loading,
    initialLoading: loading && data === undefined,
  };
}

/** Drop all cached query results — e.g. for a manual "refresh" affordance. */
export function clearQueryCache(): void {
  cache.clear();
  inflight.clear();
}

/** Drop cached results whose key starts with `prefix` — targeted invalidation
 *  after a mutation, so OTHER consumers of the same data (different versions /
 *  pages) refetch instead of serving a stale entry from the dedupe window. An
 *  in-flight request for a matching key is also dropped so the next mount
 *  starts a fresh fetch rather than adopting the pre-mutation response. */
export function invalidateQueries(prefix: string): void {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
  for (const key of inflight.keys()) {
    if (key.startsWith(prefix)) inflight.delete(key);
  }
}
